// PersonTable.jsx

import React, { useMemo, useState, useEffect } from 'react';
import ExcelUploader from './ExcelUploader';
import PersonRow from './PersonRow';
import { computeAutoAdditionsFromHours, computeFeeTotals, computeMonthlyHourSummary, computeTotal2 } from '../utils/obracunCalculator';
import { getPersonEarningsForMonth, sumEarningsByWeek } from '../utils/obracunPersonContext';
import { getMonthlyFinancial, getMonthlyScalar } from '../utils/userPeriodDataUtils';


function PersonTable({
  people = [],
  earningsData = [],
  userPeriodData = {},
  updateEarnings,
  selectedYear,
  selectedMonth,
  onUpdatePerson,
  refreshData,
  firms = [],
  selectedFirm = '',
  dailyMetricsData = [],
  monthlyNightHoursMap = {},
  holidaysByDate = {},
  obracunRates = { sunday: 0, holiday: 0, overtime: 0, night: 0 },
}) {
  // Default vrijednosti za stupce
  const defaultHiddenColumns = {
    glovoId: false,
    woltId: false,
    vozilo: true,
    registracija: true,
    vlasnik: true,
    firma: false,
    zaradaTotal: false,
    fee: false,
    feeIznos: false,
    total1: false,
    tax: false,
    rent: false,
    petrol: false,
    acom: false,
    utility: false,
    sim: false,
    advance: false,
    nedelja: false,
    praznici: false,
    prekovremeno: false,
    nocniSati: false,
    ostalo: false,
    bonus: false,
    total2: false,
  };

  // Učitaj spremljene postavke iz localStorage pri inicijalizaciji
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('personTableColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...defaultHiddenColumns, ...parsed };
      }
    } catch (e) {
      console.warn('Greška pri učitavanju postavki stupaca:', e);
    }
    return defaultHiddenColumns;
  });

  // Spremi postavke u localStorage kad se promijene
  useEffect(() => {
    try {
      localStorage.setItem('personTableColumns', JSON.stringify(hiddenColumns));
    } catch (e) {
      console.warn('Greška pri spremanju postavki stupaca:', e);
    }
  }, [hiddenColumns]);

  // Stanje za otvorenost dropdown menija
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);

  // Zatvaranje dropdown menija na ESC tipku
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isColumnMenuOpen) {
        setIsColumnMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isColumnMenuOpen]);

  // Obračun uvijek prikazuje tjedan 1-5
  const numOfWeeks = 5;
  const startWeek = 1;

  const filteredPeople = useMemo(() => {
    return people
      .filter((person) => {
        const firmMatch = selectedFirm === '' || person.firmId === selectedFirm;
        const dateMatch =
          (person.startYear < selectedYear ||
            (person.startYear === selectedYear && person.startMonth <= selectedMonth)) &&
          (!person.archived ||
            person.archiveYear > selectedYear ||
            (person.archiveYear === selectedYear && person.archiveMonth > selectedMonth));
        const notArchived = !person.archived;
        return firmMatch && dateMatch && notArchived;
      })
      .sort((a, b) => {
        if (selectedFirm === '') {
          // Sort by firm name first if no firm is selected, then by person ID
          const firmA = firms.find(firm => firm.id === a.firmId)?.name || '';
          const firmB = firms.find(firm => firm.id === b.firmId)?.name || '';
          if (firmA === firmB) {
            return a.id.localeCompare(b.id);
          }
          return firmA.localeCompare(firmB);
        } else {
          // If a firm is selected, sort only by person ID
          return a.id.localeCompare(b.id);
        }
      });
  }, [people, selectedFirm, selectedYear, selectedMonth, firms]);

  // Function to calculate sums
  const sums = useMemo(() => {
    const sums = {
      // Earnings per week
      ...Array.from({ length: numOfWeeks }, (_, i) => [`tjedan${startWeek + i}`, 0]).reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {}
      ),
      // Wolt zarade
      w1: 0,
      w2: 0,
      // Other sums
      zaradaTotal: 0,
      feeIznos: 0,
      total1: 0,
      tax: 0,
      rent: 0,
      petrol: 0,
      acom: 0,
      utility: 0,
      sim: 0,
      advance: 0,
      nedelja: 0,
      praznici: 0,
      prekovremeno: 0,
      nocniSati: 0,
      ostalo: 0,
      bonus: 0,
      total2: 0,
    };

    filteredPeople.forEach((person) => {
      const monthlyDoc = userPeriodData[person.id];
      const personEarnings = getPersonEarningsForMonth(
        earningsData,
        person.id,
        selectedYear,
        selectedMonth
      );
      const { weekTotals, zaradaTotal } = sumEarningsByWeek(personEarnings);

      Object.entries(weekTotals).forEach(([weekKey, amount]) => {
        if (sums[weekKey] !== undefined) sums[weekKey] += amount;
      });

      const fee = getMonthlyScalar(person, monthlyDoc, 'fee');
      const { feeIznos, total1 } = computeFeeTotals(zaradaTotal, fee);
      const tax = getMonthlyFinancial(person, monthlyDoc, 'tax');
      const rent = getMonthlyFinancial(person, monthlyDoc, 'rent');
      const petrol = getMonthlyFinancial(person, monthlyDoc, 'petrol');
      const acom = getMonthlyFinancial(person, monthlyDoc, 'acom');
      const utility = getMonthlyFinancial(person, monthlyDoc, 'utility');
      const sim = getMonthlyFinancial(person, monthlyDoc, 'sim');
      const advance = getMonthlyFinancial(person, monthlyDoc, 'advance');
      const ostalo = getMonthlyScalar(person, monthlyDoc, 'ostalo');
      const bonus = getMonthlyScalar(person, monthlyDoc, 'bonus');

      const hoursSummary = computeMonthlyHourSummary(
        person.id,
        selectedYear,
        selectedMonth,
        dailyMetricsData,
        holidaysByDate,
        monthlyNightHoursMap
      );
      const autoAmounts = computeAutoAdditionsFromHours(hoursSummary, obracunRates);

      const total2 = computeTotal2({
        total1,
        tax,
        rent,
        petrol,
        acom,
        utility,
        sim,
        advance,
        sunday: autoAmounts.sunday,
        holiday: autoAmounts.holiday,
        overtime: autoAmounts.overtime,
        night: autoAmounts.night,
        bonus,
        ostalo,
      });

      // Sum other fields
      sums.zaradaTotal += zaradaTotal;
      sums.feeIznos += feeIznos;
      sums.total1 += total1;
      sums.tax += tax;
      sums.rent += rent;
      sums.petrol += petrol;
      sums.acom += acom;
      sums.utility += utility;
      sums.sim += sim;
      sums.advance += advance;
      sums.nedelja += autoAmounts.sunday;
      sums.praznici += autoAmounts.holiday;
      sums.prekovremeno += autoAmounts.overtime;
      sums.nocniSati += autoAmounts.night;
      sums.ostalo += ostalo;
      sums.bonus += bonus;
      sums.total2 += total2;
    });

    return sums;
  }, [filteredPeople, earningsData, userPeriodData, numOfWeeks, startWeek, selectedYear, selectedMonth, dailyMetricsData, holidaysByDate, monthlyNightHoursMap, obracunRates]);

  // Funkcija za toggle vidljivosti stupca
  const toggleColumn = (columnName) => {
    setHiddenColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  // Definicija svih stupaca s prijateljskim imenima
  const allColumns = [
    { key: 'glovoId', label: 'Glovo ID' },
    { key: 'woltId', label: 'Wolt ID' },
    { key: 'vozilo', label: 'Vozilo' },
    { key: 'registracija', label: 'Registracija' },
    { key: 'vlasnik', label: 'Vlasnik' },
    { key: 'firma', label: 'Firma' },
    { key: 'zaradaTotal', label: 'Zarada Total' },
    { key: 'fee', label: 'Fee %' },
    { key: 'feeIznos', label: 'Fee iznos' },
    { key: 'total1', label: 'Total 1' },
    { key: 'tax', label: 'Tax' },
    { key: 'rent', label: 'Rent' },
    { key: 'petrol', label: 'Petrol' },
    { key: 'acom', label: 'Acom' },
    { key: 'utility', label: 'Utility' },
    { key: 'sim', label: 'Sim' },
    { key: 'advance', label: 'Advance' },
    { key: 'nedelja', label: 'Nedelja' },
    { key: 'praznici', label: 'Praznici' },
    { key: 'prekovremeno', label: 'Prekovremeno' },
    { key: 'nocniSati', label: 'Noćni' },
    { key: 'ostalo', label: 'Ostalo' },
    { key: 'bonus', label: 'Bonus' },
    { key: 'total2', label: 'Total 2' },
  ];

  return (
    <div className="person-table-container">
      {/* Prikaz broja osoba i kontrole za sakrivanje stupaca */}
      <div className="table-header">
        <span className="people-count">
          Prikazano osoba: {filteredPeople.length}
        </span>
        <div className="column-toggle-wrapper">
          <button 
            className="column-toggle-button"
            onClick={() => setIsColumnMenuOpen(!isColumnMenuOpen)}
          >
            <span>Prikaži stupce</span>
            <span className={`dropdown-arrow ${isColumnMenuOpen ? 'open' : ''}`}>▼</span>
          </button>
          {isColumnMenuOpen && (
            <>
              <div 
                className="dropdown-overlay"
                onClick={() => setIsColumnMenuOpen(false)}
              />
              <div className="column-dropdown-menu">
                <div className="dropdown-header">
                  <h3>Odaberi stupce za prikaz</h3>
                  <button 
                    className="close-dropdown"
                    onClick={() => setIsColumnMenuOpen(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="dropdown-content">
                  {allColumns.map((column) => (
                    <label key={column.key} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={!hiddenColumns[column.key]}
                        onChange={() => toggleColumn(column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <table className="person-table">
        <thead>
          <tr>
            <th>Ime osobe</th>
            <th>Alati</th>
            <th className={hiddenColumns.glovoId ? 'hidden-column' : ''}>Glovo ID</th>
            <th className={hiddenColumns.woltId ? 'hidden-column' : ''}>Wolt ID</th>
            <th className={hiddenColumns.vozilo ? 'hidden-column' : ''}>Vozilo</th>
            <th className={hiddenColumns.registracija ? 'hidden-column' : ''}>Registracija</th>
            <th className={hiddenColumns.vlasnik ? 'hidden-column' : ''}>Vlasnik</th>
            <th className={hiddenColumns.firma ? 'hidden-column' : ''}>Firma</th>
            {[...Array(numOfWeeks)].map((_, i) => (
              <th key={i}>
                <ExcelUploader
                  onDataParsed={(data, filename) => {
                    const weekNumber = startWeek + i;
                    console.log(
                      `Uploading data for week ${weekNumber} with filename ${filename}`
                    );
                    updateEarnings(weekNumber, data, filename);
                  }}
                >
                  Tjedan {startWeek + i}
                </ExcelUploader>
              </th>
            ))}
            <th>
              <ExcelUploader
                onDataParsed={(data, filename) => updateEarnings('w1', data, filename, 'wolt')}
              >
                W1
              </ExcelUploader>
            </th>
            <th>
              <ExcelUploader
                onDataParsed={(data, filename) => updateEarnings('w2', data, filename, 'wolt')}
              >
                W2
              </ExcelUploader>
            </th>
            <th className={hiddenColumns.zaradaTotal ? 'hidden-column' : ''}>Zarada Total</th>
            <th className={`${hiddenColumns.fee ? 'hidden-column' : ''} highlighted-header`}>Fee %</th>
            <th className={`${hiddenColumns.feeIznos ? 'hidden-column' : ''} highlighted-header`}>Fee iznos</th>
            <th className={`${hiddenColumns.total1 ? 'hidden-column' : ''} highlighted-header`}>Total 1</th>
            <th className={hiddenColumns.tax ? 'hidden-column' : ''}>Tax</th>
            <th className={hiddenColumns.rent ? 'hidden-column' : ''}>Rent</th>
            <th className={hiddenColumns.petrol ? 'hidden-column' : ''}>Petrol</th>
            <th className={hiddenColumns.acom ? 'hidden-column' : ''}>Acom</th>
            <th className={hiddenColumns.utility ? 'hidden-column' : ''}>Utility</th>
            <th className={hiddenColumns.sim ? 'hidden-column' : ''}>Sim</th>
            <th className={hiddenColumns.advance ? 'hidden-column' : ''}>Advance</th>
            <th className={hiddenColumns.nedelja ? 'hidden-column' : ''}>Nedelja</th>
            <th className={hiddenColumns.praznici ? 'hidden-column' : ''}>Praznici</th>
            <th className={hiddenColumns.prekovremeno ? 'hidden-column' : ''}>Prekovremeno</th>
            <th className={hiddenColumns.nocniSati ? 'hidden-column' : ''}>Noćni</th>
            <th className={hiddenColumns.bonus ? 'hidden-column' : ''}>Bonus</th>
            <th className={hiddenColumns.ostalo ? 'hidden-column' : ''}>Ostalo</th>
            <th className={`${hiddenColumns.total2 ? 'hidden-column' : ''} highlighted-header`}>Total 2</th>
          </tr>

          {/* Summary row appears here */}
          <tr className="summary-row">
              <td>
                <strong>Suma</strong>
              </td>
              <td></td>
              <td className={hiddenColumns.glovoId ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.woltId ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.vozilo ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.registracija ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.vlasnik ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.firma ? 'hidden-column' : ''}></td>
              {[...Array(numOfWeeks)].map((_, i) => {
                const weekKey = `tjedan${startWeek + i}`;
                return (
                  <td key={i}>
                    <strong>{sums[weekKey].toFixed(2)}</strong>
                  </td>
                );
              })}
              <td>
                <strong>{sums.w1 ? sums.w1.toFixed(2) : '0.00'}</strong>
              </td>
              <td>
                <strong>{sums.w2 ? sums.w2.toFixed(2) : '0.00'}</strong>
              </td>
              <td className={hiddenColumns.zaradaTotal ? 'hidden-column' : ''}>
                <strong>{sums.zaradaTotal.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.fee ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.feeIznos ? 'hidden-column' : ''}>
                <strong>{sums.feeIznos.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.total1 ? 'hidden-column' : ''}>
                <strong>{sums.total1.toFixed(2)}</strong>
              </td>
              <td className={hiddenColumns.tax ? 'hidden-column' : ''}><strong>{sums.tax.toFixed(2)}</strong></td>
              <td className={hiddenColumns.rent ? 'hidden-column' : ''}><strong>{sums.rent.toFixed(2)}</strong></td>
              <td className={hiddenColumns.petrol ? 'hidden-column' : ''}><strong>{sums.petrol.toFixed(2)}</strong></td>
              <td className={hiddenColumns.acom ? 'hidden-column' : ''}><strong>{sums.acom.toFixed(2)}</strong></td>
              <td className={hiddenColumns.utility ? 'hidden-column' : ''}><strong>{sums.utility.toFixed(2)}</strong></td>
              <td className={hiddenColumns.sim ? 'hidden-column' : ''}><strong>{sums.sim.toFixed(2)}</strong></td>
              <td className={hiddenColumns.advance ? 'hidden-column' : ''}><strong>{sums.advance.toFixed(2)}</strong></td>
              <td className={hiddenColumns.nedelja ? 'hidden-column' : ''}><strong>{sums.nedelja.toFixed(2)}</strong></td>
              <td className={hiddenColumns.praznici ? 'hidden-column' : ''}><strong>{sums.praznici.toFixed(2)}</strong></td>
              <td className={hiddenColumns.prekovremeno ? 'hidden-column' : ''}><strong>{sums.prekovremeno.toFixed(2)}</strong></td>
              <td className={hiddenColumns.nocniSati ? 'hidden-column' : ''}><strong>{sums.nocniSati.toFixed(2)}</strong></td>
              <td className={hiddenColumns.bonus ? 'hidden-column' : ''}><strong>{sums.bonus.toFixed(2)}</strong></td>
              <td className={hiddenColumns.ostalo ? 'hidden-column' : ''}><strong>{sums.ostalo.toFixed(2)}</strong></td>
              <td className={hiddenColumns.total2 ? 'hidden-column' : ''}>
                <strong>{sums.total2.toFixed(2)}</strong>
              </td>
            </tr>
        </thead>
        <tbody>
          {filteredPeople.length > 0 ? (
            filteredPeople.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                earningsData={earningsData}
                numOfWeeks={numOfWeeks}
                startWeek={startWeek}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                userPeriodData={userPeriodData[person.id] || {}}
                onUpdatePerson={onUpdatePerson}
                refreshData={refreshData}
                firms={firms}
                dailyMetricsData={dailyMetricsData}
                monthlyNightHoursMap={monthlyNightHoursMap}
                holidaysByDate={holidaysByDate}
                obracunRates={obracunRates}
                hiddenColumns={hiddenColumns}
              />
            ))
            ) : (
              <tr>
                <td colSpan={numOfWeeks + 28} style={{ textAlign: 'center' }}>
                  Nema prikazanih osoba.
                </td>
              </tr>
            )}
        </tbody>
      </table>
    </div>
  );
}

export default PersonTable;
